/**
 * ReferralGiftBanner — top-of-page banner shown when a friend lands on
 * /resume?ref=CODE. Displays a masked referrer chip and gifts the visitor
 * a free ATS check framing.
 *
 * Backend:  GET /api/resume-studio/referrals/lookup/{code}
 *   → { code, referrer_display: "pri****@example.com", valid: true }
 *
 * Fails soft: if the code is invalid, this component renders nothing.
 */
import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { Gift, X } from "@phosphor-icons/react";

export default function ReferralGiftBanner({ code, onDismiss }) {
    const [display, setDisplay] = useState(null);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        if (!code) return;
        fetch(`${API}/resume-studio/referrals/lookup/${encodeURIComponent(code)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (d && d.valid) setDisplay(d.referrer_display);
            })
            .catch(() => {});
    }, [code]);

    if (!code || !display || hidden) return null;

    const dismiss = () => {
        setHidden(true);
        onDismiss?.();
    };

    return (
        <div
            data-testid="rs-referral-gift-banner"
            className="mb-4 rounded-lg border border-emerald-300 bg-gradient-to-r from-emerald-50 via-teal-50 to-cobalt/5 px-4 py-3 flex items-center gap-3 relative"
        >
            <div className="shrink-0 rounded-full bg-emerald-500/15 p-1.5">
                <Gift size={18} weight="duotone" className="text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-bold text-slate-900 leading-tight">
                    🎁 <span data-testid="rs-referrer-display">{display}</span> gifted you a free AI CV check!
                </p>
                <p className="text-[11px] md:text-xs text-slate-600 mt-0.5">
                    ATS score + section-by-section breakdown, on us. Run it below.
                </p>
            </div>
            <button
                type="button"
                onClick={dismiss}
                data-testid="rs-referral-gift-dismiss"
                aria-label="Dismiss gift banner"
                className="shrink-0 text-slate-400 hover:text-slate-700 p-1"
            >
                <X size={14} weight="bold" />
            </button>
        </div>
    );
}
